/* ================================================================
   Cart Drawer — Premium v3
   Centralized delivery (delegates to shipping-banner.js),
   smart shipping banner with product suggestion,
   smooth animations — optimized: single /cart.js fetch.
   ================================================================ */

class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this._cartCache = null;   // shared cart data, avoids duplicate fetches
    this._cartCacheTs = 0;    // timestamp of last fetch
    this._CACHE_TTL = 800;    // ms — reuse cached cart within this window

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this._boundClose = this.close.bind(this);
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this._boundClose);
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');

    // Single refresh: one fetch for banner + suggestion + delivery
    this.refreshDrawerData();
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      // Re-bind overlay (use a single stored handler to avoid accumulating listeners)
      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        if (!this._boundClose) this._boundClose = this.close.bind(this);
        overlay.removeEventListener('click', this._boundClose);
        overlay.addEventListener('click', this._boundClose);
      }
      // Invalidate cache so the open() refresh fetches fresh data
      this._cartCache = null;
      this._cartCacheTs = 0;
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) { this.activeElement = element; }

  /* ── Unified refresh: ONE /cart.js fetch for everything ───── */
  refreshDrawerData(cartDataFromEvent) {
    var self = this;
    // Delivery dates are pure date math, always safe to update
    document.dispatchEvent(new CustomEvent('cart-drawer:opened'));

    // If we received cart data from a PubSub event, use it directly
    if (cartDataFromEvent && typeof cartDataFromEvent.total_price === 'number') {
      self._applyBanner(cartDataFromEvent);
      self._applySuggestion(cartDataFromEvent);
      return;
    }

    // Reuse cached data if recent enough
    var now = Date.now();
    if (self._cartCache && (now - self._cartCacheTs) < self._CACHE_TTL) {
      self._applyBanner(self._cartCache);
      self._applySuggestion(self._cartCache);
      return;
    }

    fetch('/cart.js', { credentials: 'same-origin' })
      .then(function(r) { return r.json(); })
      .then(function(cart) {
        if (!cart || typeof cart.total_price !== 'number') return;
        self._cartCache = cart;
        self._cartCacheTs = Date.now();
        self._applyBanner(cart);
        self._applySuggestion(cart);
      })
      .catch(function() {});
  }

  /* ── Apply banner state from cart data (no fetch) ──────────── */
  _applyBanner(cart) {
    const banner = document.getElementById('CartDrawer-ShipBanner');
    if (!banner) return;

    const threshold = parseInt(banner.dataset.threshold, 10) || 0;
    const shippingCost = parseInt(banner.dataset.shippingCost, 10) || 490;
    if (threshold <= 0) { banner.style.display = 'none'; return; }

    var total = cart.total_price;
    var free = total >= threshold;
    var remaining = Math.max(threshold - total, 0);
    var msgEl = banner.querySelector('.cd-ship-banner__msg');
    var fmt = this._fmt.bind(this);

    var truckSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>';
    var checkSvg = '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>';

    if (total <= 0) {
      if (msgEl) msgEl.innerHTML = '<span class="cd-ship-banner__icon cd-ship-banner__icon--truck">' + truckSvg + '</span><span>Spedizione gratuita sopra <strong>' + fmt(threshold) + '</strong></span>';
      banner.className = 'cd-ship-banner';
    } else if (free) {
      if (msgEl) msgEl.innerHTML = '<span class="cd-ship-banner__icon cd-ship-banner__icon--check">' + checkSvg + '</span><span><strong>Spedizione GRATUITA!</strong> Risparmi ' + fmt(shippingCost) + '</span>';
      banner.className = 'cd-ship-banner cd-ship-banner--free';
    } else {
      if (msgEl) msgEl.innerHTML = '<span class="cd-ship-banner__icon cd-ship-banner__icon--truck">' + truckSvg + '</span><span>Mancano solo <strong>' + fmt(remaining) + '</strong> per la spedizione gratuita!</span>';
      banner.className = 'cd-ship-banner cd-ship-banner--pending';
    }

    banner.style.display = '';
    banner.classList.remove('cd-ship-banner--pulse');
    void banner.offsetWidth;
    banner.classList.add('cd-ship-banner--pulse');
  }

  /* ── Apply suggestion from cart data (single recommendations fetch) ── */
  _applySuggestion(cart) {
    var suggestEl = document.getElementById('CartDrawer-Suggest');
    if (!suggestEl) return;
    var self = this;

    if (!cart.items || cart.items.length === 0) { suggestEl.innerHTML = ''; return; }

    var bannerEl = document.getElementById('CartDrawer-ShipBanner');
    var threshold = parseInt(bannerEl ? bannerEl.dataset.threshold : 0, 10) || 0;
    if (threshold <= 0 || cart.total_price >= threshold) { suggestEl.innerHTML = ''; return; }

    var remaining = threshold - cart.total_price;
    var cartProductIds = cart.items.map(function(i) { return i.product_id; });
    var firstProductId = cart.items[0].product_id;
    var fmt = self._fmt.bind(self);

    fetch('/recommendations/products.json?product_id=' + firstProductId + '&limit=8&intent=complementary')
      .then(function(r) { return r.json(); })
      .then(function(data) {
        if (!data.products || data.products.length === 0) { suggestEl.innerHTML = ''; return; }

        var candidates = data.products.filter(function(p) {
          return cartProductIds.indexOf(p.id) === -1 && p.available && p.variants && p.variants.length > 0;
        });
        if (candidates.length === 0) { suggestEl.innerHTML = ''; return; }

        candidates.sort(function(a, b) {
          return Math.abs(a.variants[0].price - remaining) - Math.abs(b.variants[0].price - remaining);
        });

        var pick = candidates[0];
        var variant = pick.variants[0];
        var price = fmt(parseInt(variant.price, 10));
        var img = pick.featured_image ? pick.featured_image.replace(/\.(jpg|jpeg|png|webp)/, '_180x.$1') : '';

        suggestEl.innerHTML =
          '<div class="cd-suggest">' +
            '<span class="cd-suggest__label">Aggiungi per la spedizione gratuita:</span>' +
            '<div class="cd-suggest__card">' +
              '<a href="' + pick.url + '" class="cd-suggest__media" tabindex="-1">' +
                '<img src="' + img + '" alt="' + pick.title.replace(/"/g, '&quot;') + '" loading="lazy" width="60" height="60">' +
              '</a>' +
              '<div class="cd-suggest__info">' +
                '<a href="' + pick.url + '" class="cd-suggest__title">' + pick.title + '</a>' +
                '<span class="cd-suggest__price">' + price + '</span>' +
              '</div>' +
              '<button type="button" class="cd-suggest__add" data-variant-id="' + variant.id + '" aria-label="Aggiungi ' + pick.title.replace(/"/g, '&quot;') + '">' +
                '<svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>' +
              '</button>' +
            '</div>' +
          '</div>';

        var addBtn = suggestEl.querySelector('.cd-suggest__add');
        if (addBtn) addBtn.addEventListener('click', function() { self.addSuggestedProduct(addBtn); });
      })
      .catch(function() { suggestEl.innerHTML = ''; });
  }

  addSuggestedProduct(btn) {
    var variantId = btn.dataset.variantId;
    if (!variantId) return;
    btn.disabled = true;
    btn.innerHTML = '<svg class="cd-suggest__spinner" xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10" opacity="0.25"/><path d="M12 2a10 10 0 0 1 10 10"/></svg>';
    var self = this;

    fetch(window.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ items: [{ id: parseInt(variantId, 10), quantity: 1 }], sections: self.getSectionsToRender().map(function(s) { return s.id; }) }),
    })
      .then(function(r) { return r.json(); })
      .then(function(data) {
        self._cartCache = null;
        self._cartCacheTs = 0;
        if (data.sections) self.renderContents({ id: variantId, sections: data.sections });
        else self.open();
        // Notify other components (floating banner, etc.) that the cart changed
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-drawer', cartData: data });
        }
      })
      .catch(function() { btn.disabled = false; btn.textContent = '+'; });
  }

  /* ── Money formatter ─────────────────────────────────────── */
  _fmt(cents) {
    cents = Math.max(0, Math.round(cents));
    var formatted = (cents / 100).toFixed(2).replace('.', ',');
    var moneyFmt = window.__shipMoneyFmt || '';
    if (!moneyFmt) return '\u20ac' + formatted;
    if (moneyFmt.indexOf('amount_with_comma_separator') > -1)
      return moneyFmt.replace(/\{\{\s*amount_with_comma_separator\s*\}\}/, formatted);
    if (moneyFmt.indexOf('amount') > -1)
      return moneyFmt.replace(/\{\{\s*amount\s*\}\}/, (cents / 100).toFixed(2));
    return '\u20ac' + formatted;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

/* ── Auto-update on cart changes (uses event data, no extra fetch) ── */
(function() {
  function onCartChange(eventData) {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || typeof drawer.refreshDrawerData !== 'function') return;
    // Only refresh if the drawer is currently visible
    if (!drawer.classList.contains('active')) return;
    // Pass cart data from the event to avoid redundant /cart.js fetch
    var cartData = eventData && eventData.cartData ? eventData.cartData : null;
    drawer._cartCache = null;
    drawer._cartCacheTs = 0;
    drawer.refreshDrawerData(cartData);
  }
  function bind() {
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, function(evt) { setTimeout(function() { onCartChange(evt); }, 350); });
      return true;
    }
    return false;
  }
  if (!bind()) {
    var attempts = 0;
    var iv = setInterval(function() { if (bind() || ++attempts > 50) clearInterval(iv); }, 100);
  }
})();
